import { selectAvailableDishTypesIds } from "slices/dishesSlice";
import { selectFridgeState } from "slices/fridgeInfoSlice";
import { selectHasActiveOrder, selectOrderDishes } from "slices/transactionSlice";

import { OrderProcessDrawer } from "components/appDrawer/OrderProcessDrawer";
import { EmptyFridgeModal } from "components/global/modals/EmptyFridgeModal";
import { OutOfServiceModal } from "components/global/modals/OutOfServiceModal";
import { RestrictedModal } from "components/global/modals/RestrictedModal";
import { StartingUpModal } from "components/global/modals/StartingUpModal";
import { MealList } from "components/mealList/MealList";
import { useDeviceContext } from "contexts";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { DefaultLayout } from "layouts/DefaultLayout";
import { FridgeState } from "types";

import { selectInitialDataLoaded } from "../slices/appStateSlice";

interface MealsContentProps {
	isLoading: boolean;
	hasDishes: boolean;
	showEmptyState: boolean;
	fridgeState: FridgeState | null;
	hasActiveOrder: boolean;
}

const MealsContent = ({
	isLoading,
	hasDishes,
	showEmptyState,
	fridgeState,
	hasActiveOrder,
}: MealsContentProps) => {
	if (fridgeState === FridgeState.OutOfService) {
		return <OutOfServiceModal />;
	}

	if (fridgeState === FridgeState.Restricted && !hasActiveOrder) {
		return <RestrictedModal />;
	}

	const isStartingUp =
		isLoading && !hasDishes && !(fridgeState === FridgeState.Restricted && hasActiveOrder);
	const isEmpty =
		!isLoading &&
		!hasDishes &&
		showEmptyState &&
		!(fridgeState === FridgeState.Restricted && hasActiveOrder);

	if (isStartingUp) {
		return <StartingUpModal />;
	}

	if (isEmpty) {
		return <EmptyFridgeModal />;
	}

	return (
		<>
			<MealList />
			<OrderProcessDrawer />
		</>
	);
};

export const MealsListPage = () => {
	const availableDishTypesIds = useAppSelector(selectAvailableDishTypesIds);
	const orderDishes = useAppSelector(selectOrderDishes);
	const hasActiveOrder = useAppSelector(selectHasActiveOrder);
	const fridgeState = useAppSelector(selectFridgeState);
	const { loading: isInitialQueryLoading } = useDeviceContext();
	const initialDataLoaded = useAppSelector(selectInitialDataLoaded);

	const hasDishes = availableDishTypesIds.size > 0 || orderDishes.length > 0;
	/*
	We delay the display of an empty state because it can cause false positives as the initialQuery
	can be loaded, but the available dishes can be empty before it gets data from the query
	 */
	const showEmptyState = initialDataLoaded && !hasDishes;

	return (
		<DefaultLayout>
			<MealsContent
				isLoading={isInitialQueryLoading}
				hasDishes={hasDishes}
				showEmptyState={showEmptyState}
				fridgeState={fridgeState}
				hasActiveOrder={hasActiveOrder}
			/>
		</DefaultLayout>
	);
};
